import { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import axios from "axios";
import { Star } from "lucide-react";

const API = process.env.REACT_APP_BACKEND_URL + "/api";

export default function PublicRatingPage({ kind = "rating" }) {
    const params = useParams();
    const [context, setContext] = useState(null);
    const [error, setError] = useState("");
    const [rating, setRating] = useState(0);
    const [comment, setComment] = useState("");
    const [submitted, setSubmitted] = useState(false);
    const [submitting, setSubmitting] = useState(false);

    const path = kind === "rating"
        ? `/public/rating/${params.siteId}/${params.month}/${params.token}`
        : `/public/feedback/${params.siteId}/${params.token}`;

    useEffect(() => {
        axios.get(`${API}${path}/context`)
            .then((res) => setContext(res.data))
            .catch(() => setError("Sorry — this feedback link is not valid or has expired."));
    }, [path]);

    async function submit(e) {
        e.preventDefault();
        if (!rating) return setError("Please pick a rating.");
        setSubmitting(true);
        try {
            await axios.post(`${API}${path}`, { rating, comment });
            setSubmitted(true); setError("");
        } catch (err) { setError("Could not submit. Please try again."); }
        finally { setSubmitting(false); }
    }

    if (error && !context) return <PublicShell><ErrorBox message={error} /></PublicShell>;
    if (!context) return <PublicShell><div className="h-40 rounded-md bg-white animate-pulse" /></PublicShell>;
    if (submitted) return (
        <PublicShell>
            <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-8 text-center" data-testid="public-thanks">
                <div className="text-4xl mb-2">🙏</div>
                <div className="font-display text-xl font-bold text-[#1E3355]">Thank you</div>
                <div className="text-sm text-gray-600 mt-1">Your feedback has been recorded. We appreciate it.</div>
            </div>
        </PublicShell>
    );
    return (
        <PublicShell>
            <div className="rounded-xl bg-white border border-gray-100 fx-shadow-card p-6" data-testid="public-form">
                <div className="text-xs font-medium uppercase tracking-widest text-gray-500">{kind === "rating" ? "Client feedback" : "Member feedback"}</div>
                <h1 className="font-display text-2xl font-bold text-[#1E3355] mt-1">{context.site.name}</h1>
                {kind === "rating" && <div className="text-xs text-gray-500 mt-1">Month: {context.month}</div>}
                {context.already_submitted && <div className="mt-3 text-xs text-amber-700 bg-amber-50 border border-amber-200 px-2 py-1.5 rounded">A response for this month is already on file — you can submit an updated one.</div>}
                {error && <div className="mt-3 text-sm text-[#E02B2B]" data-testid="public-error">{error}</div>}
                <form onSubmit={submit} className="mt-5 space-y-4">
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">How was your experience?</label>
                        <div className="flex gap-2" data-testid="star-row">
                            {[1, 2, 3, 4, 5].map((n) => (
                                <button
                                    type="button" key={n} onClick={() => setRating(n)}
                                    className={`w-10 h-10 rounded-full border transition-colors flex items-center justify-center ${rating >= n ? "bg-[#E02B2B] text-white border-[#E02B2B]" : "bg-white text-gray-400 border-gray-200 hover:border-[#E02B2B]"}`}
                                    data-testid={`star-${n}`}
                                >
                                    <Star className="w-5 h-5" fill={rating >= n ? "currentColor" : "none"} />
                                </button>
                            ))}
                        </div>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">Comment (optional)</label>
                        <textarea value={comment} onChange={(e) => setComment(e.target.value)} rows={3} className="w-full rounded-md border border-gray-300 px-3 py-2 text-sm bg-white focus:outline-none focus:border-[#1E3355] focus:ring-1 focus:ring-[#1E3355]" placeholder="What worked well or could improve?" data-testid="public-comment" />
                    </div>
                    <button type="submit" disabled={submitting} className="w-full bg-[#1E3355] hover:bg-[#15243C] text-white font-medium py-2.5 rounded-md disabled:opacity-60" data-testid="public-submit">
                        {submitting ? "Sending…" : "Submit feedback"}
                    </button>
                </form>
            </div>
        </PublicShell>
    );
}

function ErrorBox({ message }) {
    return (
        <div className="rounded-xl bg-white border border-red-200 p-6 text-center">
            <div className="text-3xl">⚠️</div>
            <div className="mt-2 text-sm text-[#E02B2B]" data-testid="public-error">{message}</div>
        </div>
    );
}

function PublicShell({ children }) {
    return (
        <div className="min-h-screen bg-[#F4F6F9] flex flex-col items-center py-12 px-4">
            <div className="mb-4 flex items-center gap-3">
                <div className="w-10 h-10 rounded-lg bg-[#E02B2B] flex items-center justify-center text-white font-display font-bold">F</div>
                <div>
                    <div className="font-display font-bold text-[#1E3355]">Flexworx Ops</div>
                    <div className="text-[11px] text-gray-500">A Flexworx Company</div>
                </div>
            </div>
            <div className="w-full max-w-md">{children}</div>
        </div>
    );
}
